import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, type Location } from "react-router-dom";
import {
  fetchRemoteAppVersion,
  getLocalBuildId,
  isNewerRemoteBuild,
} from "@/lib/appVersion";

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

interface AppUpdateContextValue {
  pendingUpdate: boolean;
  reloadNow: () => void;
}

const AppUpdateContext = createContext<AppUpdateContextValue>({
  pendingUpdate: false,
  reloadNow: () => {
    window.location.reload();
  },
});

export function useAppUpdate(): AppUpdateContextValue {
  return useContext(AppUpdateContext);
}

// Nur der Pfad zählt als echte Navigation. Query-/Hash-Änderungen (Filter,
// Tab-Wechsel, Suche) passieren mitten auf der Seite – ein harter Reload würde
// den Benutzer dort herausreißen, deshalb lösen sie kein Update-Reload aus.
function routeKey(location: Location): string {
  return location.pathname;
}

function AppUpdateNavigationEffect() {
  const location = useLocation();
  const { pendingUpdate } = useAppUpdate();
  const lastRouteKeyRef = useRef(routeKey(location));
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    const nextKey = routeKey(location);
    if (!bootstrappedRef.current) {
      bootstrappedRef.current = true;
      lastRouteKeyRef.current = nextKey;
      return;
    }
    if (!pendingUpdate) {
      lastRouteKeyRef.current = nextKey;
      return;
    }
    if (nextKey === lastRouteKeyRef.current) return;

    const target = `${location.pathname}${location.search}${location.hash}`;
    window.location.assign(target);
  }, [location, pendingUpdate]);

  return null;
}

export function AppUpdateProvider({ children }: { children: ReactNode }) {
  const [pendingUpdate, setPendingUpdate] = useState(false);
  const localBuildIdRef = useRef(getLocalBuildId());
  const checkingRef = useRef(false);

  const checkForUpdate = useCallback(async () => {
    if (import.meta.env.DEV) return;
    if (checkingRef.current) return;
    checkingRef.current = true;
    try {
      const remote = await fetchRemoteAppVersion();
      if (!remote) return;
      if (isNewerRemoteBuild(localBuildIdRef.current, remote.buildId)) {
        setPendingUpdate(true);
      }
    } finally {
      checkingRef.current = false;
    }
  }, []);

  const reloadNow = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    if (import.meta.env.DEV) return;

    void checkForUpdate();

    const intervalId = window.setInterval(() => {
      void checkForUpdate();
    }, CHECK_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void checkForUpdate();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
    };
  }, [checkForUpdate]);

  const value = useMemo(
    () => ({
      pendingUpdate,
      reloadNow,
    }),
    [pendingUpdate, reloadNow],
  );

  return (
    <AppUpdateContext.Provider value={value}>
      <AppUpdateNavigationEffect />
      {children}
    </AppUpdateContext.Provider>
  );
}
